/**
 * Canonical same-day contract for generated rashifal HTML (rule 64).
 *
 * The live guard, exporter, and static edge worker all consume this module so
 * the serving fallback cannot use a weaker definition of "fresh" than the
 * release gate.
 */

export type RashifalExpectedDates = {
  iso: string;
  titleUs: string;
  longUs: string;
  longIn: string;
  longHi: string;
};

type FreshnessOptions = {
  requiredRenderer?: string;
};

export function isDailyRashifalHubPath(pathname: string): boolean {
  return pathname === "/rashifal/" || pathname === "/hi/aaj-ka-rashifal/";
}

/**
 * The pages that carry the same-day serving contract: the two daily hubs.
 * The 24 daily per-sign pages (`/rashifal/<rashi>/`, `/hi/rashifal/<rashi>/`,
 * engine-rendered 2026-09-08 to 2026-10-25) carried it too until the owner
 * merged them into these hubs; they answer a 301 now, which no same-day stamp
 * applies to.
 */
export function hasSameDayRashifalContract(pathname: string): boolean {
  return isDailyRashifalHubPath(pathname);
}

export function todayIstParts(now = new Date()): { year: string; month: string; day: string } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return { year: value.year, month: value.month, day: value.day };
}

export function expectedDates(now = new Date()): RashifalExpectedDates {
  const { year, month, day } = todayIstParts(now);
  const date = new Date(`${year}-${month}-${day}T00:00:00+05:30`);
  return {
    iso: `${year}-${month}-${day}`,
    titleUs: new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Kolkata",
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date),
    longUs: new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date),
    longIn: new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date),
    longHi: new Intl.DateTimeFormat("hi-IN", {
      timeZone: "Asia/Kolkata",
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date),
  };
}

export function secondsUntilNextIstDate(now = new Date()): number {
  const { year, month, day } = todayIstParts(now);
  const nextMidnight = new Date(`${year}-${month}-${day}T00:00:00+05:30`);
  nextMidnight.setUTCDate(nextMidnight.getUTCDate() + 1);
  return Math.max(0, Math.floor((nextMidnight.getTime() - now.getTime()) / 1000));
}

/**
 * The same-day cache policy of a page under the same-day contract
 * (`hasSameDayRashifalContract`): a browser may reuse it for a minute and a
 * shared cache for five, never past India midnight, and must then revalidate.
 * The page's own origin policy (`stale-while-revalidate=604800` on /rashifal/)
 * would let an edge serve a week-old render of a page that is only ever right
 * for today (rule 64).
 *
 * Stamped by the static-SEO Worker on the hubs and sign pages it fronted and,
 * since C09 (2026-10), by the app Worker on the same paths
 * (scripts/templates/opennext-worker-hooks.js). The installer copies this file,
 * types stripped, beside the OpenNext Worker, so it must import nothing.
 */
export function rashifalCacheControl(now = new Date()): string {
  const untilTomorrow = secondsUntilNextIstDate(now);
  const browserSeconds = Math.min(60, untilTomorrow);
  const edgeSeconds = Math.min(300, untilTomorrow);
  return `public, max-age=${browserSeconds}, s-maxage=${edgeSeconds}, must-revalidate`;
}

function stripNonVisible(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<noscript[\s\S]*?<\/noscript>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function titleText(html: string): string {
  return (
    html
      .match(/<title[^>]*>([\s\S]*?)<\/title>/i)?.[1]
      ?.replace(/\s+/g, " ")
      .trim() || ""
  );
}

function metaContent(html: string, key: string): string {
  const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return (
    html.match(
      new RegExp(`<meta[^>]+(?:name|property)=["']${escaped}["'][^>]+content=["']([^"']*)`, "i")
    )?.[1] ??
    html.match(
      new RegExp(`<meta[^>]+content=["']([^"']*)["'][^>]+(?:name|property)=["']${escaped}["']`, "i")
    )?.[1] ??
    ""
  );
}

function replaceTagText(html: string, tag: string, transform: (value: string) => string): string {
  return html.replace(
    new RegExp(`(<${tag}[^>]*>)([\\s\\S]*?)(</${tag}>)`, "i"),
    (_match, open, value, close) => `${open}${transform(value)}${close}`
  );
}

function replaceMetaContent(
  html: string,
  key: string,
  transform: (value: string) => string
): string {
  const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(
    `(<meta[^>]+(?:name|property)=["']${escaped}["'][^>]+content=["'])([^"']*)(["'][^>]*>)`,
    "i"
  );
  return html.replace(
    pattern,
    (_match, open, value, close) => `${open}${transform(value)}${close}`
  );
}

const EN_DATE_SOURCE =
  "(?:Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday), (?:January|February|March|April|May|June|July|August|September|October|November|December) \\d{1,2}, 20\\d{2}";
const HI_DATE_SOURCE =
  "(?:रविवार|सोमवार|मंगलवार|बुधवार|गुरुवार|शुक्रवार|शनिवार),? \\d{1,2} (?:जनवरी|फ़रवरी|मार्च|अप्रैल|मई|जून|जुलाई|अगस्त|सितंबर|अक्टूबर|नवंबर|दिसंबर) 20\\d{2}";
const DATE_SEPARATOR = "\\s*[—.-]\\s*";

/**
 * The three shapes a dated head field can take. `leading` is the description
 * form (`<date>. …`) and the pre-2026-09-08 title form; `trailing` is the
 * title form the app emits now (`… — <date>`); `any` locates the date itself.
 */
type DatePatterns = { any: RegExp; leading: RegExp; trailing: RegExp };

function datePatterns(source: string): DatePatterns {
  return {
    any: new RegExp(source),
    leading: new RegExp(`^${source}(?:${DATE_SEPARATOR})?`),
    trailing: new RegExp(`(?:${DATE_SEPARATOR})?${source}\\s*$`),
  };
}

const EN_DATE = datePatterns(EN_DATE_SOURCE);
const HI_DATE = datePatterns(HI_DATE_SOURCE);

/** The value with any leading or trailing date removed, whichever shape the artifact carries. */
function undatedValue(value: string, patterns: DatePatterns): string {
  return value.replace(patterns.leading, "").replace(patterns.trailing, "").trim();
}

/**
 * Title form: page name first, date LAST — the shape `withDateStamp()` in
 * src/i18n/index.ts emits, so the origin and the R2-fallback render agree.
 */
function datedTitle(value: string, date: string, patterns: DatePatterns): string {
  return `${undatedValue(value, patterns)} — ${date}`;
}

/** Description form: the date leads, as a sentence. */
function datedDescription(value: string, date: string, patterns: DatePatterns): string {
  return `${date}. ${undatedValue(value, patterns)}`;
}

const TITLE_META_KEYS = ["og:title", "twitter:title"] as const;
const DESCRIPTION_META_KEYS = ["description", "og:description", "twitter:description"] as const;

/** Keep a daily hub current even when its R2 artifact came from an older app build. */
export function normalizeDailyRashifalHubHtml(
  html: string,
  pathname: string,
  now = new Date()
): { html: string; changed: boolean } {
  if (!isDailyRashifalHubPath(pathname)) return { html, changed: false };
  const dates = expectedDates(now);
  const isHindi = pathname.startsWith("/hi/");
  const displayDate = isHindi ? dates.longHi : dates.titleUs;
  const patterns = isHindi ? HI_DATE : EN_DATE;
  // Wherever the stale artifact carried its date (leading on old builds,
  // trailing on current ones), the same date string is replaced site-wide below.
  const oldDate = titleText(html).match(patterns.any)?.[0]?.trim();
  let normalized = replaceTagText(html, "title", (value) =>
    datedTitle(value, displayDate, patterns)
  );
  for (const key of TITLE_META_KEYS) {
    normalized = replaceMetaContent(normalized, key, (value) =>
      datedTitle(value, displayDate, patterns)
    );
  }
  for (const key of DESCRIPTION_META_KEYS) {
    normalized = replaceMetaContent(normalized, key, (value) =>
      datedDescription(value, displayDate, patterns)
    );
  }
  normalized = normalized.replace(
    /("dateModified"\s*:\s*")20\d{2}-\d{2}-\d{2}("?)/g,
    `$1${dates.iso}$2`
  );
  // Must match the dateline each hub page renders, so the origin and the
  // R2-fallback render carry identical text. The EN hub also renders
  // EditorialTrustNote ("Page updated: <copy date>"), which is a different
  // fact — hence "Today's transits" rather than a second "updated" claim.
  const visible = isHindi ? `आज अपडेट: ${displayDate}` : `Today’s transits: ${displayDate}`;
  const marker = `data-ishvaram-rashifal-hub-date="${dates.iso}"`;
  const datedParagraph = `<p ${marker}>${visible}</p>`;
  if (/data-ishvaram-rashifal-hub-date=["'][^"']+["']/.test(normalized)) {
    normalized = normalized.replace(
      /<p\s+data-ishvaram-rashifal-hub-date=["'][^"']+["'][^>]*>[\s\S]*?<\/p>/i,
      datedParagraph
    );
  } else {
    normalized = normalized.replace(/(<main\b[^>]*>)/i, `$1${datedParagraph}`);
  }
  if (oldDate && oldDate !== displayDate) {
    const escapedOldDate = oldDate.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    normalized = normalized.replace(new RegExp(escapedOldDate, "g"), displayDate);
  }
  return { html: normalized, changed: normalized !== html };
}

export function inspectDailyRashifalHubFreshness(
  html: string,
  expected = expectedDates()
): ReturnType<typeof inspectFreshness> {
  const title = titleText(html);
  const description = metaContent(html, "description");
  const visibleText = stripNonVisible(html);
  const expectedLongDates = [expected.titleUs, expected.longUs, expected.longIn, expected.longHi];
  const failures: string[] = [];
  if (!expectedLongDates.some((date) => title.includes(date))) {
    failures.push(`title missing current IST date; title=${JSON.stringify(title)}`);
  }
  if (!expectedLongDates.some((date) => description.includes(date))) {
    failures.push("meta description missing current IST date");
  }
  if (
    !html.includes(`data-ishvaram-rashifal-hub-date="${expected.iso}"`) ||
    !expectedLongDates.some((date) => visibleText.includes(date))
  ) {
    failures.push(`visible hub freshness marker missing ${expected.iso}`);
  }
  if (!new RegExp(`"dateModified"\\s*:\\s*"${expected.iso}"`).test(html)) {
    failures.push(`structured dateModified missing ${expected.iso}`);
  }
  return { ok: failures.length === 0, failures, title, visibleText, seenIso: [], seenLong: [] };
}

function rendererFailures(html: string, requiredRenderer: string): string[] {
  if (!requiredRenderer) return [];
  if (requiredRenderer !== "d1") {
    return [`unsupported required rashifal renderer: ${requiredRenderer}`];
  }
  if (/data-ishvaram-static-rashifal-main=["']true["']/.test(html)) {
    return ["canonical D1 renderer required; observed deterministic static rashifal fallback"];
  }
  if (!/data-ishvaram-owned-page-view=["']d1["']/.test(html)) {
    return ["canonical D1 renderer required; D1 provenance marker is missing"];
  }
  return [];
}

const RETIRED_PLACEHOLDER = /temporarily unavailable/i;

export function inspectFreshness(
  html: string,
  expected = expectedDates(),
  options: FreshnessOptions = {}
) {
  const title = titleText(html);
  const visibleText = stripNonVisible(html);
  const seenIso = [...new Set(html.match(/20\d{2}-\d{2}-\d{2}/g) || [])];
  const seenLong = [
    ...new Set(
      visibleText.match(
        /(?:January|February|March|April|May|June|July|August|September|October|November|December) \d{1,2}, 20\d{2}|\d{1,2} (?:January|February|March|April|May|June|July|August|September|October|November|December) 20\d{2}/g
      ) || []
    ),
  ];
  const staleIso = seenIso.filter((date) => date !== expected.iso);
  const staleLong = seenLong.filter((date) => date !== expected.longUs && date !== expected.longIn);

  const failures = rendererFailures(html, options.requiredRenderer || "");
  if (/data-ishvaram-static-generic-main=["']true["']/.test(html)) {
    failures.push("generated rashifal page served generic static shell");
  }
  if (!title.includes(expected.iso)) {
    failures.push(`title missing ${expected.iso}; title=${JSON.stringify(title)}`);
  }
  if (!visibleText.includes(expected.longUs) && !visibleText.includes(expected.longIn)) {
    failures.push(
      `visible body missing ${expected.longUs}/${expected.longIn}; saw ${seenLong.slice(0, 6).join(", ") || "no long dates"}`
    );
  }
  if (staleIso.length > 0) {
    failures.push(`HTML contains stale ISO date(s): ${staleIso.slice(0, 6).join(", ")}`);
  }
  if (staleLong.length > 0) {
    failures.push(`visible body contains stale long date(s): ${staleLong.slice(0, 6).join(", ")}`);
  }
  // 2026-09-08: 72 sign pages dated today carried the provider-outage
  // placeholder as their forecast and passed every date check above. A page
  // is not fresh when its body is an error message.
  if (RETIRED_PLACEHOLDER.test(visibleText)) {
    failures.push("visible body carries the retired rashifal placeholder");
  }

  return { ok: failures.length === 0, failures, title, visibleText, seenIso, seenLong };
}
